import React, { useState, useRef } from 'react';
import { 
  ArrowLeft, Calendar, Users, Code, Layout, FileText, Search, 
  Smartphone, Globe, Settings, CheckCircle, Zap, Target, 
  Database, Shield, Palette, Cpu, Clock, TrendingUp,
  Play, Pause, Smartphone as PhoneIcon, Monitor, Maximize2,
  ExternalLink, Github, Video, Image as ImageIcon, ChevronRight,
  Star, Award, BarChart, Heart, Eye, MessageCircle, Download,
  Mail, MapPin, Phone, Globe as GlobeIcon, Server, Cpu as CpuIcon,
  Wifi, ShieldCheck, Zap as ZapIcon, Cloud, BarChart3,
  Grid, Layers, Database as DatabaseIcon, Terminal,
  Shield as ShieldIcon, WifiOff, MousePointerClick
} from 'lucide-react';

export default function AbsProject({ darkMode, language, onBack }) {
  const [activeSection, setActiveSection] = useState('overview');
  const [playingVideo, setPlayingVideo] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(0);
  
  const videoRefPC = useRef(null);
  const videoRefMobile = useRef(null);
  
  const isRTL = language === 'ar';

  const handleVideoPlay = (type) => {
    if (type === 'pc') {
      if (videoRefPC.current) {
        if (playingVideo === 'pc') {
          videoRefPC.current.pause();
          setPlayingVideo(null);
        } else {
          videoRefPC.current.play();
          setPlayingVideo('pc');
          if (videoRefMobile.current) {
            videoRefMobile.current.pause();
            videoRefMobile.current.currentTime = 0;
          }
        }
      }
    } else if (type === 'mobile') {
      if (videoRefMobile.current) {
        if (playingVideo === 'mobile') {
          videoRefMobile.current.pause();
          setPlayingVideo(null);
        } else {
          videoRefMobile.current.play();
          setPlayingVideo('mobile');
          if (videoRefPC.current) {
            videoRefPC.current.pause();
            videoRefPC.current.currentTime = 0;
          }
        }
      }
    }
  };

  const handleVideoPause = (type) => {
    if (type === 'pc' && videoRefPC.current) {
      videoRefPC.current.pause();
      if (playingVideo === 'pc') setPlayingVideo(null);
    } else if (type === 'mobile' && videoRefMobile.current) {
      videoRefMobile.current.pause();
      if (playingVideo === 'mobile') setPlayingVideo(null);
    }
  };

  const content = {
    fr: {
      title: 'ABS Compétences - Site Web d\'Entreprise',
      subtitle: 'Site corporate moderne avec CMS personnalisé, SEO avancé et design responsive',
      backButton: 'Retour aux projets',
      
      // Navigation
      navOverview: 'Vue d\'ensemble',
      navDemo: 'Démonstration',
      navDesign: 'Design & UX',
      navFeatures: 'Fonctionnalités',
      navTech: 'Technologies',
      navSeo: 'SEO & Performance',
      navResults: 'Résultats',
      
      // Hero Stats
      stats: [
        { value: '100/100', label: 'Score SEO', icon: Search },
        { value: '<2s', label: 'Chargement', icon: Zap },
        { value: '100%', label: 'Responsive', icon: Smartphone },
        { value: 'A+', label: 'Sécurité', icon: Shield }
      ],
      
      // Overview
      overviewTitle: 'Projet ABS Compétences',
      overviewIntro: 'Développement complet d\'un site web corporate pour ABS Compétences, spécialisée dans la formation professionnelle. Solution sur-mesure avec CMS personnalisé, système de blog et optimisation SEO avancée.',
      
      projectGoals: 'Objectifs du Projet',
      goalsList: [
        'Créer une présence en ligne professionnelle et moderne',
        'Développer un CMS intuitif pour la gestion autonome',
        'Optimiser le référencement naturel (SEO)',
        'Assurer une expérience utilisateur exceptionnelle',
        'Intégrer un système de blog pour partager des expertises'
      ],
      
      myRole: 'Mon Rôle - Développeur Full Stack Solo',
      roleDetails: [
        'Conception de l\'architecture MVC avec Laravel',
        'Développement frontend avec Tailwind CSS et JavaScript',
        'Création du système de gestion de contenu personnalisé',
        'Optimisation SEO technique et sémantique',
        'Développement des fonctionnalités backend',
        'Base de données MySQL avec optimisation',
        'Déploiement et configuration serveur'
      ],
      
      // Demonstration Section
      demoTitle: 'Démonstration Responsive',
      demoSubtitle: 'Voir le site en action sur PC et mobile',
      
      pcDemo: 'Version Desktop',
      pcDescription: 'Expérience complète sur grand écran avec toutes les fonctionnalités',
      mobileDemo: 'Version Mobile',
      mobileDescription: 'Interface optimisée pour smartphones avec navigation tactile',
      
      videoInstructions: 'Cliquez sur les boutons Play pour voir les démos',
      
      responsiveFeatures: 'Caractéristiques Responsives',
      responsiveFeaturesList: [
        'Navigation adaptative (menu burger mobile)',
        'Images responsive avec srcset optimisé',
        'Typographie adaptative en rem/vw',
        'Grille fluide (Flexbox/Grid)',
        'Boutons et formulaires touch-friendly'
      ],
      
      responsiveStats: 'Statistiques Responsive',
      responsiveStatsList: [
        { icon: Smartphone, label: 'Mobile First', value: 'Oui' },
        { icon: Monitor, label: 'Desktop Optimisé', value: 'Oui' },
        { icon: Globe, label: 'Cross-browser', value: '100%' },
        { icon: Wifi, label: 'Performance 4G', value: 'A+' }
      ],
      
      // Design Section
      designTitle: 'Design & Expérience Utilisateur',
      designSubtitle: 'Interface moderne et intuitive optimisée pour l\'engagement',
      
      designPrinciples: 'Principes de Design',
      principlesList: [
        {
          title: 'Modernité & Professionnalisme',
          description: 'Design épuré avec palette de couleurs corporate reflétant l\'image de marque'
        },
        {
          title: 'Navigation Intuitive',
          description: 'Structure claire avec menus intelligents pour une expérience fluide'
        },
        {
          title: 'Accessibilité',
          description: 'Conforme aux standards WCAG 2.1 pour tous les utilisateurs'
        },
        {
          title: 'Consistance',
          description: 'Design system cohérent à travers toutes les pages'
        }
      ],
      
      responsiveDesign: 'Design Responsive Adaptatif',
      responsiveDescription: 'Le site s\'adapte parfaitement à tous les appareils : desktop, tablette et mobile',
      
      // Features Section
      featuresTitle: 'Fonctionnalités Avancées',
      featuresSubtitle: 'Système complet pour la gestion de contenu et l\'engagement',
      
      featuresList: [
        {
          icon: Settings,
          title: 'CMS Personnalisé',
          description: 'Interface d\'administration intuitive permettant de gérer toutes les sections du site sans compétences techniques',
          details: [
            'Éditeur WYSIWYG avec TinyMCE',
            'Gestion des images avec redimensionnement automatique',
            'Gestion des pages dynamiques',
            'Prévisualisation en temps réel',
            'Sauvegarde automatique'
          ]
        },
        {
          icon: FileText,
          title: 'Système de Blog',
          description: 'Plateforme complète pour publier des articles, actualités et études de cas',
          details: [
            'Catégories et tags',
            'Commentaires et interactions',
            'Recherche avancée',
            'Partage social',
            'Planification des publications'
          ]
        },
        {
          icon: Users,
          title: 'Gestion Multi-utilisateurs',
          description: 'Système de rôles et permissions pour équipe collaborative',
          details: [
            'Rôles personnalisés (Admin, Éditeur, Contributeur)',
            'Permissions granulaires',
            'Historique des modifications',
            'Notifications d\'activité',
            'Gestion des profils'
          ]
        },
        {
          icon: Search,
          title: 'SEO Avancé',
          description: 'Optimisation complète pour les moteurs de recherche',
          details: [
            'Meta tags dynamiques',
            'Sitemap XML automatique',
            'Structure des données (Schema.org)',
            'URL optimisées',
            'Rapports SEO intégrés'
          ]
        },
        {
          icon: Globe,
          title: 'Performance Optimisée',
          description: 'Site ultra-rapide avec optimisations techniques avancées',
          details: [
            'Cache intelligent',
            'Compression des assets',
            'Chargement différé (lazy loading)',
            'Optimisation des images',
            'CDN intégré'
          ]
        },
        {
          icon: Shield,
          title: 'Sécurité Renforcée',
          description: 'Protection complète contre les menaces en ligne',
          details: [
            'HTTPS obligatoire',
            'Protection CSRF',
            'Validation des données',
            'Sauvegardes automatiques',
            'Monitoring de sécurité'
          ]
        }
      ],
      
      // Technologies
      techTitle: 'Stack Technologique',
      techSubtitle: 'Technologies modernes et robustes',
      
      frontendTech: {
        title: 'Frontend',
        technologies: [
          { name: 'Laravel Blade', purpose: 'Templates serveur' },
          { name: 'Tailwind CSS', purpose: 'Styling utilitaire' },
          { name: 'Alpine.js', purpose: 'Interactivité' },
          { name: 'Chart.js', purpose: 'Visualisations' }
        ]
      },
      
      backendTech: {
        title: 'Backend',
        technologies: [
          { name: 'Laravel 10', purpose: 'Framework PHP' },
          { name: 'MySQL', purpose: 'Base de données' },
          { name: 'Redis', purpose: 'Cache' },
          { name: 'Eloquent ORM', purpose: 'Modèles de données' }
        ]
      },
      
      devOpsTech: {
        title: 'DevOps & Outils',
        technologies: [
          { name: 'Git', purpose: 'Version control' },
          { name: 'Composer', purpose: 'Gestion des dépendances' },
          { name: 'NPM', purpose: 'Gestion des packages' },
          { name: 'Webpack', purpose: 'Bundling' }
        ]
      },
      
      // SEO Section
      seoTitle: 'Stratégie SEO & Performance',
      seoSubtitle: 'Optimisation complète pour la visibilité en ligne',
      
      seoStrategy: 'Stratégie SEO Implémentée',
      strategyPoints: [
        {
          title: 'SEO Technique',
          items: [
            'Structure HTML5 sémantique',
            'URL optimisées et lisibles',
            'Sitemap XML dynamique',
            'robots.txt configuré',
            'Balises canoniques'
          ]
        },
        {
          title: 'SEO On-Page',
          items: [
            'Meta titles et descriptions dynamiques',
            'Balises H1-H6 optimisées',
            'Images avec alt text',
            'Contenu structuré',
            'Liens internes optimisés'
          ]
        },
        {
          title: 'Performance',
          items: [
            'Score Google PageSpeed > 95',
            'Temps de chargement < 2s',
            'Optimisation Core Web Vitals',
            'Compression GZIP/Brotli',
            'Cache HTTP efficace'
          ]
        }
      ],
      
      performanceMetrics: 'Métriques de Performance',
      metrics: [
        { metric: 'Largest Contentful Paint (LCP)', value: '1.2s', target: '<2.5s', status: 'excellent' },
        { metric: 'First Input Delay (FID)', value: '15ms', target: '<100ms', status: 'excellent' },
        { metric: 'Cumulative Layout Shift (CLS)', value: '0.05', target: '<0.1', status: 'excellent' },
        { metric: 'First Contentful Paint (FCP)', value: '0.8s', target: '<1.8s', status: 'excellent' }
      ],
      
      // Results
      resultsTitle: 'Résultats & Impact',
      resultsSubtitle: 'Performance et bénéfices mesurables',
      
      beforeAfter: 'Avant/Après',
      improvements: [
        {
          metric: 'Temps de chargement',
          before: '5.2s',
          after: '1.8s',
          improvement: '+65%'
        },
        {
          metric: 'Score SEO',
          before: '45/100',
          after: '95/100',
          improvement: '+111%'
        },
        {
          metric: 'Trafic organique',
          before: '120/mois',
          after: '450/mois',
          improvement: '+275%'
        },
        {
          metric: 'Taux de conversion',
          before: '1.2%',
          after: '3.8%',
          improvement: '+217%'
        }
      ],
      
      clientTestimonial: {
        quote: 'Le site développé par Abdelmajid a complètement transformé notre présence en ligne. Le CMS est si intuitif que même nos collaborateurs non-techniques peuvent facilement mettre à jour le contenu. Les résultats SEO ont dépassé nos attentes.',
        author: 'Directeur ABS Compétences',
        position: 'Client'
      },
      
      keyAchievements: 'Réalisations Clés',
      achievementsList: [
        'Développement complet en solo en 3 mois',
        'CMS personnalisé répondant exactement aux besoins',
        'Performance optimisée pour tous les appareils',
        'Formation complète de l\'équipe client',
        'Support et maintenance continues'
      ],
      
      // CTA
      ctaTitle: 'Vous avez un projet similaire ?',
      ctaSubtitle: 'Travaillons ensemble pour créer votre solution web sur mesure',
      ctaButton: 'Discuter de votre projet',
      ctaContact: 'Contactez-moi',
      
      projectInfo: {
        duration: '3 mois',
        status: 'Live & Maintenu',
        team: 'Développeur solo',
        stack: 'Laravel, MySQL, Tailwind'
      }
    },
    ar: {
      title: 'ABS Compétences - موقع شركة',
      subtitle: 'موقع شركة حديث مع نظام إدارة محتوى مخصص، SEO متقدم وتصميم متجاوب',
      backButton: 'العودة للمشاريع',
      
      // Navigation
      navOverview: 'نظرة عامة',
      navDemo: 'التجربة',
      navDesign: 'التصميم',
      navFeatures: 'المميزات',
      navTech: 'التقنيات',
      navSeo: 'SEO والأداء',
      navResults: 'النتائج',
      
      // Hero Stats
      stats: [
        { value: '100/100', label: 'نتيجة SEO', icon: Search },
        { value: '<2ث', label: 'التحميل', icon: Zap },
        { value: '100%', label: 'متجاوب', icon: Smartphone },
        { value: 'A+', label: 'الأمان', icon: Shield }
      ],
      
      // Overview
      overviewTitle: 'مشروع ABS Compétences',
      overviewIntro: 'تطوير كامل لموقع شركة لـ ABS Compétences المتخصصة في التدريب المهني. حل مخصص مع نظام إدارة محتوى، نظام مدونة وتحسين SEO متقدم.',
      
      projectGoals: 'أهداف المشروع',
      goalsList: [
        'إنشاء حضور إلكتروني احترافي وحديث',
        'تطوير نظام إدارة محتوى بديهي',
        'تحسين محركات البحث (SEO)',
        'ضمان تجربة مستخدم استثنائية',
        'دمج نظام مدونة لمشاركة الخبرات'
      ],
      
      myRole: 'دوري - مطور Full Stack منفرد',
      roleDetails: [
        'تصميم بنية MVC مع Laravel',
        'تطوير الواجهة بـ Tailwind CSS و JavaScript',
        'إنشاء نظام إدارة محتوى مخصص',
        'تحسين SEO التقني والدلالي',
        'تطوير وظائف الخادم الخلفي',
        'قاعدة بيانات MySQL مع تحسين',
        'النشر وتهيئة الخادم'
      ],
      
      // Demonstration Section
      demoTitle: 'تجربة التجاوب',
      demoSubtitle: 'شاهد الموقع يعمل على الكمبيوتر والهاتف',
      
      pcDemo: 'نسخة الكمبيوتر',
      pcDescription: 'تجربة كاملة على الشاشات الكبيرة مع جميع الميزات',
      mobileDemo: 'نسخة الهاتف',
      mobileDescription: 'واجهة محسنة للهواتف مع تنقل باللمس',
      
      videoInstructions: 'انقر على أزرار التشغيل لمشاهدة العروض',
      
      responsiveFeatures: 'ميزات التجاوب',
      responsiveFeaturesList: [
        'تنقل تكيفي (قائمة برجر على الهاتف)',
        'صور متجاوبة مع srcset محسن',
        'طباعة تكيفية بـ rem/vw',
        'شبكة مرنة (Flexbox/Grid)',
        'أزرار ونماذج سهلة اللمس'
      ],
      
      responsiveStats: 'إحصائيات التجاوب',
      responsiveStatsList: [
        { icon: Smartphone, label: 'Mobile First', value: 'نعم' },
        { icon: Monitor, label: 'محسن للكمبيوتر', value: 'نعم' },
        { icon: Globe, label: 'Cross-browser', value: '100%' },
        { icon: Wifi, label: 'أداء 4G', value: 'A+' }
      ],
      
      // Design Section
      designTitle: 'التصميم وتجربة المستخدم',
      designSubtitle: 'واجهة حديثة وبديهية محسنة للتفاعل',
      
      designPrinciples: 'مبادئ التصميم',
      principlesList: [
        {
          title: 'الحداثة والاحترافية',
          description: 'تصميم أنيق بألوان تعكس صورة العلامة التجارية'
        },
        {
          title: 'تنقل بديهي',
          description: 'هيكل واضح مع قوائم ذكية لتجربة سلسة'
        },
        {
          title: 'إمكانية الوصول',
          description: 'متوافق مع معايير WCAG 2.1 لجميع المستخدمين'
        },
        {
          title: 'التناسق',
          description: 'نظام تصميم متناسق عبر جميع الصفحات'
        }
      ],
      
      responsiveDesign: 'تصميم متجاوب تكيفي',
      responsiveDescription: 'الموقع يتكيف بشكل مثالي مع جميع الأجهزة: كمبيوتر، لوحي وهاتف',
      
      // Features Section
      featuresTitle: 'ميزات متقدمة',
      featuresSubtitle: 'نظام كامل لإدارة المحتوى والتفاعل',
      
      featuresList: [
        {
          icon: Settings,
          title: 'نظام إدارة محتوى مخصص',
          description: 'واجهة إدارة بديهية تمكن من إدارة جميع أقسام الموقع بدون مهارات تقنية',
          details: [
            'محرر WYSIWYG مع TinyMCE',
            'إدارة الصور مع تغيير الحجم التلقائي',
            'إدارة الصفحات الديناميكية',
            'معاينة في الوقت الفعلي',
            'حفظ تلقائي'
          ]
        },
        {
          icon: FileText,
          title: 'نظام المدونة',
          description: 'منصة كاملة لنشر المقالات والأخبار ودراسات الحالة',
          details: [
            'فئات ووسوم',
            'تعليقات وتفاعلات',
            'بحث متقدم',
            'مشاركة اجتماعية',
            'جدولة المنشورات'
          ]
        },
        {
          icon: Users,
          title: 'إدارة متعددة المستخدمين',
          description: 'نظام أدوار وأذونات للعمل التعاوني',
          details: [
            'أدوار مخصصة (مدير، محرر، مساهم)',
            'أذونات مفصلة',
            'سجل التعديلات',
            'إشعارات النشاط',
            'إدارة الملفات الشخصية'
          ]
        },
        {
          icon: Search,
          title: 'SEO متقدم',
          description: 'تحسين كامل لمحركات البحث',
          details: [
            'علامات Meta ديناميكية',
            'خريطة موقع XML تلقائية',
            'هيكل البيانات (Schema.org)',
            'عنوان URL محسن',
            'تقارير SEO مدمجة'
          ]
        },
        {
          icon: Globe,
          title: 'أداء محسن',
          description: 'موقع فائق السرعة مع تحسينات تقنية متقدمة',
          details: [
            'كاش ذكي',
            'ضغط الأصول',
            'تحميل متأخر (lazy loading)',
            'تحسين الصور',
            'CDN مدمج'
          ]
        },
        {
          icon: Shield,
          title: 'أمان معزز',
          description: 'حماية كاملة ضد التهديدات الإلكترونية',
          details: [
            'HTTPS إلزامي',
            'حماية CSRF',
            'تحقق من البيانات',
            'نسخ احتياطي تلقائي',
            'مراقبة الأمان'
          ]
        }
      ],
      
      // Technologies
      techTitle: 'المجموعة التقنية',
      techSubtitle: 'تقنيات حديثة وقوية',
      
      frontendTech: {
        title: 'الواجهة الأمامية',
        technologies: [
          { name: 'Laravel Blade', purpose: 'قوالب الخادم' },
          { name: 'Tailwind CSS', purpose: 'التصميم' },
          { name: 'Alpine.js', purpose: 'التفاعلية' },
          { name: 'Chart.js', purpose: 'التصورات' }
        ]
      },
      
      backendTech: {
        title: 'الخادم الخلفي',
        technologies: [
          { name: 'Laravel 10', purpose: 'إطار عمل PHP' },
          { name: 'MySQL', purpose: 'قاعدة البيانات' },
          { name: 'Redis', purpose: 'الكاش' },
          { name: 'Eloquent ORM', purpose: 'نماذج البيانات' }
        ]
      },
      
      devOpsTech: {
        title: 'DevOps والأدوات',
        technologies: [
          { name: 'Git', purpose: 'التحكم بالإصدار' },
          { name: 'Composer', purpose: 'إدارة التبعيات' },
          { name: 'NPM', purpose: 'إدارة الحزم' },
          { name: 'Webpack', purpose: 'التجميع' }
        ]
      },
      
      // SEO Section
      seoTitle: 'إستراتيجية SEO والأداء',
      seoSubtitle: 'تحسين كامل للظهور الإلكتروني',
      
      seoStrategy: 'إستراتيجية SEO المطبقة',
      strategyPoints: [
        {
          title: 'SEO التقني',
          items: [
            'هيكل HTML5 دلالي',
            'عنوان URL محسن وقابل للقراءة',
            'خريطة موقع XML ديناميكية',
            'robots.txt مهيأ',
            'علامات Canonical'
          ]
        },
        {
          title: 'SEO داخل الصفحة',
          items: [
            'عناوين ووصوف Meta ديناميكية',
            'علامات H1-H6 محسنة',
            'صور مع نص بديل',
            'محتوى منظم',
            'روابط داخلية محسنة'
          ]
        },
        {
          title: 'الأداء',
          items: [
            'نتيجة Google PageSpeed > 95',
            'وقت التحميل < 2ث',
            'تحسين Core Web Vitals',
            'ضغط GZIP/Brotli',
            'كاش HTTP فعال'
          ]
        }
      ],
      
      performanceMetrics: 'مقاييس الأداء',
      metrics: [
        { metric: 'Largest Contentful Paint (LCP)', value: '1.2ث', target: '<2.5ث', status: 'ممتاز' },
        { metric: 'First Input Delay (FID)', value: '15ملث', target: '<100ملث', status: 'ممتاز' },
        { metric: 'Cumulative Layout Shift (CLS)', value: '0.05', target: '<0.1', status: 'ممتاز' },
        { metric: 'First Contentful Paint (FCP)', value: '0.8ث', target: '<1.8ث', status: 'ممتاز' }
      ],
      
      // Results
      resultsTitle: 'النتائج والتأثير',
      resultsSubtitle: 'أداء وفوائد قابلة للقياس',
      
      beforeAfter: 'قبل/بعد',
      improvements: [
        {
          metric: 'وقت التحميل',
          before: '5.2ث',
          after: '1.8ث',
          improvement: '+65%'
        },
        {
          metric: 'نتيجة SEO',
          before: '45/100',
          after: '95/100',
          improvement: '+111%'
        },
        {
          metric: 'الزيارات العضوية',
          before: '120/شهر',
          after: '450/شهر',
          improvement: '+275%'
        },
        {
          metric: 'معدل التحويل',
          before: '1.2%',
          after: '3.8%',
          improvement: '+217%'
        }
      ],
      
      clientTestimonial: {
        quote: 'الموقع الذي طوره عبد المجيد غير تمامًا وجودتنا على الإنترنت. نظام إدارة المحتوى بديهي لدرجة أن حتى الموظفين غير التقنيين يمكنهم تحديث المحتوى بسهولة. نتائج SEO تجاوزت توقعاتنا.',
        author: 'مدير ABS Compétences',
        position: 'العميل'
      },
      
      keyAchievements: 'الإنجازات الرئيسية',
      achievementsList: [
        'تطوير كامل منفرد في 3 أشهر',
        'نظام إدارة محتوى مخصص يلبي الاحتياجات بدقة',
        'أداء محسن لجميع الأجهزة',
        'تدريب كامل لفريق العميل',
        'دعم وصيانة مستمرة'
      ],
      
      // CTA
      ctaTitle: 'هل لديك مشروع مشابه؟',
      ctaSubtitle: 'لنعمل معًا لإنشاء حل ويب مخصص لك',
      ctaButton: 'مناقشة مشروعك',
      ctaContact: 'اتصل بي',
      
      projectInfo: {
        duration: '3 أشهر',
        status: 'نشط ومحافظ عليه',
        team: 'مطور منفرد',
        stack: 'Laravel, MySQL, Tailwind'
      }
    }
  };

  const t = content[language];

  const sections = [
    { id: 'overview', label: t.navOverview, icon: Eye },
    { id: 'demo', label: t.navDemo, icon: Video },
    { id: 'design', label: t.navDesign, icon: Palette },
    { id: 'features', label: t.navFeatures, icon: Settings },
    { id: 'tech', label: t.navTech, icon: Code },
    { id: 'seo', label: t.navSeo, icon: Search },
    { id: 'results', label: t.navResults, icon: TrendingUp }
  ];

  const renderSectionContent = () => {
    switch(activeSection) {
      case 'overview':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.overviewTitle}</h2>
              <p className={`text-base md:text-lg leading-relaxed mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t.overviewIntro}
              </p>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-4 text-blue-500">{t.projectGoals}</h3>
              <div className="space-y-3">
                {t.goalsList.map((goal, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Target size={20} className="text-blue-500 flex-shrink-0 mt-1" />
                    <p className={`text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{goal}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-4 text-blue-500">{t.myRole}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {t.roleDetails.map((detail, index) => (
                  <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className={`text-xs md:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'demo':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.demoTitle}</h2>
              <p className={`text-base md:text-lg mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.demoSubtitle}
              </p>
              <p className={`text-sm md:text-base mb-6 ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                {t.videoInstructions}
              </p>
            </div>

            {/* Desktop Demo - PC */}
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <Monitor size={32} className="text-blue-500" />
                  <h3 className="text-xl md:text-2xl font-bold text-blue-500">{t.pcDemo}</h3>
                </div>
                <div className="flex-1">
                  <p className={`text-base md:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t.pcDescription}
                  </p>
                </div>
              </div>
              
              {/* Desktop Video */}
              <div className={`aspect-video rounded-xl overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-100'} border-2 ${darkMode ? 'border-gray-700' : 'border-gray-300'} mb-4`}>
                <video
                  ref={videoRefPC}
                  className="w-full h-full object-contain"
                  controls
                  onPlay={() => handleVideoPlay('pc')}
                  onPause={() => handleVideoPause('pc')}
                  poster="/thumbnails/pc-abs.jpg"
                >
                  <source src="/video/pc-abs.mp4" type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => handleVideoPlay('pc')}
                  className={`px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 flex-1 sm:flex-none ${
                    playingVideo === 'pc'
                      ? 'bg-blue-600 text-white'
                      : darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  {playingVideo === 'pc' ? (
                    <>
                      <Pause size={20} />
                      <span className="hidden sm:inline">En cours...</span>
                      <span className="sm:hidden">Pause</span>
                    </>
                  ) : (
                    <>
                      <Play size={20} />
                      <span>Voir démo PC</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleVideoPause('pc')}
                  className={`px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 flex-1 sm:flex-none ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  <Pause size={20} />
                  <span>Arrêter</span>
                </button>
              </div>
            </div>

            {/* Mobile Demo - Téléphone */}
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <Smartphone size={32} className="text-blue-500" />
                  <h3 className="text-xl md:text-2xl font-bold text-blue-500">{t.mobileDemo}</h3>
                </div>
                <div className="flex-1">
                  <p className={`text-base md:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t.mobileDescription}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1">
                  {/* Mobile Video */}
                  <div className={`aspect-video lg:aspect-auto lg:h-[500px] rounded-xl overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-100'} border-2 ${darkMode ? 'border-gray-700' : 'border-gray-300'} mb-4`}>
                    <video
                      ref={videoRefMobile}
                      className="w-full h-full object-contain"
                      controls
                      onPlay={() => handleVideoPlay('mobile')}
                      onPause={() => handleVideoPause('mobile')}
                      poster="/thumbnails/telephone-abs.jpg"
                    >
                      <source src="/video/telephone-abs.mp4" type="video/mp4" />
                      Votre navigateur ne supporte pas la lecture de vidéos.
                    </video>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => handleVideoPlay('mobile')}
                      className={`px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 flex-1 sm:flex-none ${
                        playingVideo === 'mobile'
                          ? 'bg-blue-600 text-white'
                          : darkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      {playingVideo === 'mobile' ? (
                        <>
                          <Pause size={20} />
                          <span className="hidden sm:inline">En cours...</span>
                          <span className="sm:hidden">Pause</span>
                        </>
                      ) : (
                        <>
                          <Play size={20} />
                          <span>Voir démo mobile</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleVideoPause('mobile')}
                      className={`px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 flex-1 sm:flex-none ${
                        darkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      <Pause size={20} />
                      <span>Arrêter</span>
                    </button>
                  </div>
                </div>
                
                <div className="flex-1">
                  <h4 className="text-lg md:text-xl font-bold mb-4 text-blue-500">{t.responsiveFeatures}</h4>
                  <div className="space-y-3 mb-6">
                    {t.responsiveFeaturesList.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                        <p className={`text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{feature}</p>
                      </div>
                    ))}
                  </div>
                  
                  <h4 className="text-lg md:text-xl font-bold mb-4 text-blue-500">{t.responsiveStats.title}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {t.responsiveStatsList.map((stat, index) => {
                      const Icon = stat.icon;
                      return (
                        <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                          <Icon size={24} className="mx-auto mb-2 text-blue-500" />
                          <div className="text-sm font-medium">{stat.label}</div>
                          <div className="text-lg font-bold text-blue-500">{stat.value}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Comparison */}
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gradient-to-br from-blue-900/30 to-indigo-900/30' : 'bg-gradient-to-br from-blue-50 to-indigo-50'} border-2 border-blue-500`}>
              <h3 className="text-xl md:text-2xl font-bold mb-4 text-blue-500 text-center">Responsive Design Comparison</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} text-center`}>
                  <Monitor size={32} className="mx-auto mb-3 text-blue-500" />
                  <h4 className="font-bold text-lg mb-2">Desktop</h4>
                  <ul className="text-sm space-y-1 text-left">
                    <li className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Navigation horizontale</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Images haute résolution</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Hover effects</span>
                    </li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} text-center`}>
                  <Smartphone size={32} className="mx-auto mb-3 text-blue-500" />
                  <h4 className="font-bold text-lg mb-2">Mobile</h4>
                  <ul className="text-sm space-y-1 text-left">
                    <li className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Menu burger</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Touch targets larges</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Optimisation 4G</span>
                    </li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} text-center`}>
                  <Globe size={32} className="mx-auto mb-3 text-blue-500" />
                  <h4 className="font-bold text-lg mb-2">Tablette</h4>
                  <ul className="text-sm space-y-1 text-left">
                    <li className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Navigation hybride</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Interface adaptative</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Touch & souris</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'design':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.designTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.designSubtitle}
              </p>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.designPrinciples}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {t.principlesList.map((principle, index) => (
                  <div key={index} className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} hover:scale-105 transition-transform`}>
                    <h4 className="font-bold text-lg mb-2 text-blue-500">{principle.title}</h4>
                    <p className={`text-sm md:text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {principle.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.responsiveDesign}</h3>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t.responsiveDescription}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                  <Monitor size={40} className="mx-auto mb-4 text-blue-500" />
                  <h4 className="font-bold text-lg mb-2">Desktop</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Expérience complète</p>
                </div>
                <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                  <Smartphone size={40} className="mx-auto mb-4 text-blue-500" />
                  <h4 className="font-bold text-lg mb-2">Mobile</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Navigation tactile</p>
                </div>
                <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                  <Maximize2 size={40} className="mx-auto mb-4 text-blue-500" />
                  <h4 className="font-bold text-lg mb-2">Tablette</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Format hybride</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.featuresTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.featuresSubtitle}
              </p>
            </div>

            <div className="space-y-6">
              {t.featuresList.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                    <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                      <div className="p-3 md:p-4 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 flex-shrink-0">
                        <Icon size={28} className="md:w-8 md:h-8" color="white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl md:text-2xl font-bold mb-3 text-blue-500">{feature.title}</h3>
                        <p className={`text-base md:text-lg mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {feature.description}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {feature.details.map((detail, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                              <p className={`text-sm md:text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{detail}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'tech':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.techTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.techSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                <h3 className="text-xl font-bold mb-6 text-blue-500">{t.frontendTech.title}</h3>
                <div className="space-y-4">
                  {t.frontendTech.technologies.map((tech, index) => (
                    <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="font-bold text-blue-500">{tech.name}</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{tech.purpose}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                <h3 className="text-xl font-bold mb-6 text-blue-500">{t.backendTech.title}</h3>
                <div className="space-y-4">
                  {t.backendTech.technologies.map((tech, index) => (
                    <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="font-bold text-blue-500">{tech.name}</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{tech.purpose}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                <h3 className="text-xl font-bold mb-6 text-blue-500">{t.devOpsTech.title}</h3>
                <div className="space-y-4">
                  {t.devOpsTech.technologies.map((tech, index) => (
                    <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="font-bold text-blue-500">{tech.name}</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{tech.purpose}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">Architecture MVC Laravel</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                  <Cpu size={32} className="mx-auto mb-3 text-blue-500" />
                  <h4 className="font-bold mb-2">Modèle</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>MySQL avec Eloquent ORM</p>
                </div>
                <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                  <Layout size={32} className="mx-auto mb-3 text-blue-500" />
                  <h4 className="font-bold mb-2">Vue</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Blade + Tailwind CSS</p>
                </div>
                <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                  <Settings size={32} className="mx-auto mb-3 text-blue-500" />
                  <h4 className="font-bold mb-2">Contrôleur</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Laravel Controllers</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'seo':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.seoTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.seoSubtitle}
              </p>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.seoStrategy}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {t.strategyPoints.map((strategy, index) => (
                  <div key={index} className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h4 className="font-bold text-lg mb-4 text-blue-500">{strategy.title}</h4>
                    <div className="space-y-2">
                      {strategy.items.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.performanceMetrics}</h3>
              <div className="space-y-4">
                {t.metrics.map((metric, index) => (
                  <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-2">
                      <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {metric.metric}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                        metric.status === 'excellent' 
                          ? 'bg-green-500/20 text-green-600'
                          : 'bg-yellow-500/20 text-yellow-600'
                      }`}>
                        {metric.value}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between text-sm gap-1">
                      <span className={darkMode ? 'text-gray-500' : 'text-gray-600'}>
                        Cible: {metric.target}
                      </span>
                      <span className="text-blue-500 font-bold">✓ Atteint</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'results':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.resultsTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.resultsSubtitle}
              </p>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.beforeAfter}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {t.improvements.map((imp, index) => (
                  <div key={index} className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                    <div className="text-lg md:text-xl font-bold mb-2 text-blue-500">{imp.metric}</div>
                    <div className="flex items-center justify-center gap-3 md:gap-4 mb-3">
                      <div className="text-center">
                        <div className="text-xs md:text-sm text-gray-500 mb-1">Avant</div>
                        <div className={`text-base md:text-lg font-bold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {imp.before}
                        </div>
                      </div>
                      <ChevronRight size={20} className="text-blue-500" />
                      <div className="text-center">
                        <div className="text-xs md:text-sm text-gray-500 mb-1">Après</div>
                        <div className={`text-lg md:text-xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                          {imp.after}
                        </div>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-bold bg-green-500/20 text-green-600`}>
                      {imp.improvement}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">Témoignage Client</h3>
              <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-500`}>
                <MessageCircle size={32} className="text-blue-500 mb-4" />
                <blockquote className={`text-lg md:text-xl italic leading-relaxed mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  "{t.clientTestimonial.quote}"
                </blockquote>
                <p className="font-bold text-lg">{t.clientTestimonial.author}</p>
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                  {t.clientTestimonial.position}
                </p>
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.keyAchievements}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {t.achievementsList.map((achievement, index) => (
                  <div key={index} className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                    <Award size={28} className="mx-auto mb-3 text-blue-500" />
                    <p className={`font-medium text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {achievement}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} py-4 md:py-8`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        {/* Back Button */}
        <button
          onClick={onBack}
          className={`flex items-center gap-2 mb-6 px-3 py-2 md:px-4 md:py-2 rounded-lg ${
            darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'
          } transition-colors shadow-lg text-sm md:text-base`}
        >
          <ArrowLeft size={18} className="md:w-5 md:h-5" />
          <span>{t.backButton}</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8 md:mb-12 px-2">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 bg-gradient-to-r from-blue-500 to-indigo-600 bg-clip-text text-transparent">
            {t.title}
          </h1>
          <p className={`text-base sm:text-lg md:text-xl ${darkMode ? 'text-gray-400' : 'text-gray-600'} px-2`}>
            {t.subtitle}
          </p>
        </div>

        {/* Hero Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-12">
          {t.stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className={`p-4 md:p-6 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                <Icon size={20} className="mx-auto mb-2 text-blue-500 md:w-6 md:h-6" />
                <div className="text-lg md:text-2xl font-bold text-blue-500">{stat.value}</div>
                <div className={`text-xs md:text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation - Responsive */}
        <div className="mb-6 md:mb-8 overflow-x-auto">
          <div className="flex gap-1 md:gap-2 pb-2 min-w-max">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-3 rounded-lg font-semibold transition-all whitespace-nowrap text-sm md:text-base ${
                    activeSection === section.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : darkMode
                      ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                      : 'bg-white hover:bg-gray-100 text-gray-700 shadow-md'
                  }`}
                >
                  <Icon size={16} className="md:w-5 md:h-5" />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="mb-8 md:mb-12">
          {renderSectionContent()}
        </div>

        {/* Project Info */}
        <div className={`mt-6 md:mt-8 p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 text-center">
            <div>
              <CheckCircle size={18} className="mx-auto mb-2 text-blue-500 md:w-5 md:h-5" />
              <div className="font-bold text-blue-500 text-sm md:text-base">{t.projectInfo.status}</div>
              <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Statut</div>
            </div>
            <div>
              <Users size={18} className="mx-auto mb-2 text-blue-500 md:w-5 md:h-5" />
              <div className="font-bold text-blue-500 text-sm md:text-base">{t.projectInfo.team}</div>
              <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Équipe</div>
            </div>
            <div>
              <Code size={18} className="mx-auto mb-2 text-blue-500 md:w-5 md:h-5" />
              <div className="font-bold text-blue-500 text-sm md:text-base">{t.projectInfo.stack}</div>
              <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Stack</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}